import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { verifyOrgEmailChange } from '../../../services/company'
import { useTheme } from '../../../context/ThemeContext'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useAuth } from '../core/Auth'
import packageInfo from '../../../../../package.json'

interface TokenData {
  id: string
  organizationId?: string
  newEmail: string
  oldEmail: string
  type: string
  iat: number
  exp: number
}

export function VerifyOrgEmailChange(): React.JSX.Element {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const { currentUser } = useAuth()

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')

  const token = searchParams?.get('token')

  const decodeToken = (token: string): TokenData | null => {
    try {
      const payload = token?.split('.')[1]
      const base64 = payload?.replace(/-/g, '+')?.replace(/_/g, '/')
      const padded = base64 + '='.repeat((4 - (base64?.length % 4)) % 4)
      return JSON?.parse(atob(padded))
    } catch (error) {
      return null
    }
  }

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error')
        setMessage('Verification token is missing. Please check your email link.')
        return
      }

      const tokenData = decodeToken(token)
      if (!tokenData) {
        setStatus('error')
        setMessage('Invalid token format. Please check your email link.')
        return
      }

      // If a different user's organization is logged in, redirect to 404
      const tokenOrgId = tokenData.id || tokenData.organizationId
      if (currentUser && currentUser.organizationId && tokenOrgId) {
        if (currentUser.organizationId !== tokenOrgId) {
          navigate('/error/404')
          return
        }
      }

      try {
        const response: any = await verifyOrgEmailChange(token)
        if (response?.data?.success) {
          setStatus('success')
          setMessage(response?.data?.message || 'Organization email has been changed successfully!')
          setTimeout(() => {
            navigate('/manage-account/licenses')
          }, 5000)
        } else {
          setStatus('error')
          setMessage(response?.data?.message || 'Organization email verification failed. Please try again.')
        }
      } catch (error: any) {
        setStatus('error')
        const errorMessage =
          error?.response?.data?.message ||
          'Invalid or expired token. Please request a new organization email change.'
        setMessage(errorMessage)
      }
    }

    verifyEmail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUser])

  return (
    <div className={`d-flex ${theme === 'dark' ? 'dark__div' : ''} flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed`}>
      <div className={`${theme === 'dark' ? 'dark__div' : ''} d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-8`}>
        <div className={`${theme === 'dark' ? 'layout__  theme__div' : ''} w-lg-550px w-sm-550px rounded shadow-sm p-lg-15 p-md-10 p-sm-7 p-5 mx-auto text-center`} style={{ width: '100%' }}>
        <div className="text-center mb-10">
          <div className="d-flex align-items-center justify-content-center gap-5">
            <h1 className="text-dark mt-7">
              <span className={theme === 'dark' ? 'white__text' : ''}>Welcome to</span>{' '}
              <span className={`fw-semibold ${theme === 'dark' ? 'white__text' : ''}`}>b4i</span>
              <span className="text-primary fw-semibold">go</span>
            </h1>
            <a className="">
              <img
                alt="Logo"
                src={toAbsoluteUrl('/media/logos/b4igo-go.png')}
                className="h-100px w-90px my-5 me-15"
              />
            </a>
          </div>
        </div>
        <h2 className={`fw-bolder fs-2 mb-5 ${theme === 'dark' ? 'white__text' : 'text-dark'}`}>
          Organization Email Change Verification
        </h2>

        {status === 'loading' && (
          <div className="py-10">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className={`mt-5 fs-5 ${theme === 'dark' ? 'white__text' : 'text-muted'}`}>
              Verifying your organization email change request...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="py-10">
            <div className="mb-5">
              <i
                className="bi bi-check-circle-fill text-success"
                style={{ fontSize: '4rem' }}
              ></i>
            </div>
            <div className="alert alert-success" role="alert">
              <h4 className="alert-heading">Success!</h4>
              <p className="mb-0">{message}</p>
            </div>
            <p className={`mt-5 fs-6 ${theme === 'dark' ? 'white__text' : 'text-muted'}`}>
              You will be redirected to the account page in 5 seconds...
            </p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => navigate('/manage-account/licenses')}
            >
              Go to Manage Account
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="py-10">
            <div className="mb-5">
              <i
                className="bi bi-x-circle-fill text-danger"
                style={{ fontSize: '4rem' }}
              ></i>
            </div>
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Verification Failed</h4>
              <p className="mb-0">{message}</p>
            </div>
            <div className="mt-5">
              <button
                className="btn btn-primary"
                onClick={() => navigate(currentUser ? '/manage-account/licenses' : '/auth')}
              >
                {currentUser ? 'Go to Manage Account' : 'Go to Login'}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
      {!currentUser && (
        <div className='d-flex flex-center flex-column-auto p-10'>
          <div className='d-flex align-items-center fw-bold fs-6'>
            <a
              href={`${process.env.REACT_APP_B4IGO_LANDING_PAGE}/#about`}
              className='text-muted text-hover-primary px-2'
            >
              About
            </a>
            <a
              href={`${process.env.REACT_APP_B4IGO_LANDING_PAGE}/#footer-contact`}
              className='text-muted text-hover-primary px-2'
            >
              Contact
            </a>
            <span className='text-muted text-hover-primary px-2'>Version: {packageInfo.version}</span>
          </div>
        </div>
      )}
    </div>
  )
}
